import React, { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'ios' | 'android' | 'desktop' | 'unknown';

function PWAInstallBanner(): React.ReactElement | null {
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [platform, setPlatform] = useState<Platform>('unknown');
  const [isInstalled, setIsInstalled] = useState<boolean>(false);

  useEffect(() => {
    // Skip all banner logic during react-snap prerendering. Chrome DOES fire
    // beforeinstallprompt in puppeteer, and the 2s setTimeout below would otherwise
    // bake the banner into the static HTML, which mismatches the client's initial
    // (banner-less) render and breaks hydration (React #418/#423).
    if (typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap')) {
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if user dismissed the banner
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Show again after 7 days
    if (dismissed && daysSinceDismissed < 7) {
      return;
    }

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    if (isIOS) {
      setPlatform('ios');
      // Show banner for iOS after a short delay
      setTimeout(() => setShowBanner(true), 2000);
    } else if (isAndroid) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Listen for the beforeinstallprompt event (Android/Desktop Chrome)
    const handleBeforeInstall = (e: Event): void => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setShowBanner(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowBanner(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async (): Promise<void> => {
    if (deferredPrompt) {
      // Android/Desktop Chrome
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else if (platform === 'ios') {
      // Show iOS instructions
      setShowInstructions(true);
    }
  };

  const handleDismiss = (): void => {
    setShowBanner(false);
    setShowInstructions(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  if (isInstalled || !showBanner) {
    return null;
  }

  return (
    <>
      {/* Install Banner */}
      <div className="pwa-banner">
        <div className="pwa-banner-content">
          <div className="pwa-banner-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm-1 6h2v2h-2V8zm0 4h2v6h-2v-6z"/>
            </svg>
          </div>
          <div className="pwa-banner-text">
            <strong>Install FancyMyText</strong>
            <span>Add to home screen for quick access</span>
          </div>
          <div className="pwa-banner-actions">
            <button className="pwa-install-btn" onClick={handleInstallClick}>
              Install
            </button>
            <button className="pwa-dismiss-btn" onClick={handleDismiss} aria-label="Dismiss">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showInstructions && (
        <div className="pwa-modal-overlay" onClick={handleDismiss}>
          <div className="pwa-modal" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <button className="pwa-modal-close" onClick={handleDismiss} aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>

            <h2>Install FancyMyText</h2>
            <p>Add this app to your home screen for instant access anytime.</p>

            {platform === 'ios' ? (
              <div className="pwa-instructions">
                <div className="pwa-step">
                  <div className="pwa-step-number">1</div>
                  <div className="pwa-step-content">
                    <p>Tap the <strong>Share</strong> button in Safari</p>
                    <div className="pwa-step-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V10c0-1.1.9-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .9 2 2z"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="pwa-step">
                  <div className="pwa-step-number">2</div>
                  <div className="pwa-step-content">
                    <p>Scroll down and tap <strong>"Add to Home Screen"</strong></p>
                    <div className="pwa-step-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="pwa-step">
                  <div className="pwa-step-number">3</div>
                  <div className="pwa-step-content">
                    <p>Tap <strong>"Add"</strong> to confirm</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="pwa-instructions">
                <div className="pwa-step">
                  <div className="pwa-step-number">1</div>
                  <div className="pwa-step-content">
                    <p>Tap the <strong>menu</strong> button (three dots)</p>
                  </div>
                </div>
                <div className="pwa-step">
                  <div className="pwa-step-number">2</div>
                  <div className="pwa-step-content">
                    <p>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></p>
                  </div>
                </div>
                <div className="pwa-step">
                  <div className="pwa-step-number">3</div>
                  <div className="pwa-step-content">
                    <p>Tap <strong>"Add"</strong> or <strong>"Install"</strong> to confirm</p>
                  </div>
                </div>
              </div>
            )}

            <div className="pwa-modal-footer">
              <button className="pwa-got-it-btn" onClick={handleDismiss}>Got it</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PWAInstallBanner;
